import { useEffect, useState } from "react";
import { Badge, Pencil, Plus, Search, Trash2 } from "lucide-react";
import axiosInstance from "../../api/axiosInstance";
import CatalogModal from "./CatalogModal";
import { PAGE_SIZE, buttonClass, errorMessage } from "./catalogUtils";

const ENDPOINT = "/api/superadmin/catalog/brands/";

export default function CatalogBrands() {
  const [brands, setBrands] = useState([]);
  const [count, setCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const formOpen = form !== null;

  useEffect(() => {
    if (search.trim() === query) return;
    const timer = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [search, query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axiosInstance.get(ENDPOINT, { params: { q: query, is_active: status, page, page_size: PAGE_SIZE } })
      .then(({ data }) => {
        if (cancelled) return;
        if (!Array.isArray(data.results) || typeof data.count !== "number") {
          throw new Error("Invalid brand list response");
        }
        setBrands(data.results);
        setCount(data.count);
        setHasNext(Boolean(data.next));
        setHasPrevious(Boolean(data.previous));
      })
      .catch((requestError) => {
        if (!cancelled) {
          setBrands([]);
          setError(requestError.message === "Invalid brand list response"
            ? "The server returned an invalid brand list. Please retry."
            : errorMessage(requestError));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query, status, page, revision]);

  function refresh() {
    setPage(1);
    setRevision((value) => value + 1);
  }

  async function editBrand(brand) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const { data } = await axiosInstance.get(`${ENDPOINT}${brand.id}/`);
      setForm(data.data);
      setDeleting(null);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  async function saveBrand(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const body = { name: form.name.trim(), is_active: form.is_active };
      if (form.id) {
        await axiosInstance.patch(`${ENDPOINT}${form.id}/`, body);
      } else {
        await axiosInstance.post(ENDPOINT, body);
      }
      setNotice(form.id ? "Brand updated successfully." : "Brand created successfully.");
      setForm(null);
      refresh();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  async function deleteBrand() {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await axiosInstance.delete(`${ENDPOINT}${deleting.id}/`);
      setDeleting(null);
      setNotice("Brand deactivated successfully. Existing product links are preserved.");
      refresh();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-6 sm:px-[30px] sm:py-7">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-slate-900">Brands</h1>
          <p className="mt-1 text-sm text-slate-500">Manage master catalog brands for your products.</p>
        </div>
        <button type="button" disabled={busy} onClick={() => {
          setForm({ name: "", is_active: true }); setDeleting(null); setError(""); setNotice("");
        }} className={`${buttonClass} flex items-center gap-2 bg-blue-500 text-white hover:bg-blue-600`}>
          <Plus className="h-4 w-4" aria-hidden="true" /> Create Brand
        </button>
      </header>

      {error && !formOpen && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
      {notice && <div role="status" className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700">{notice}</div>}

      {form && (
        <CatalogModal title={form.id ? "Edit Brand" : "Create Brand"} titleId="brand-modal-title"
          closeLabel="Close brand modal" busy={busy} onClose={() => { setForm(null); setError(""); }}>
        <form aria-label={form.id ? "Edit brand" : "Create brand"} onSubmit={saveBrand}>
          {error && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
          <label htmlFor="brand-name" className="mb-1 block text-sm font-medium text-slate-700">Brand name</label>
          <input data-initial-focus id="brand-name" required maxLength={150} disabled={busy} value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            className="w-full max-w-lg rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100" />
          <p className="mt-2 text-xs text-slate-500">
            {form.slug_name ? `Slug: ${form.slug_name} (generated automatically)` : "The slug is generated automatically when you save."}
          </p>
          <label className="my-4 flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_active} disabled={busy}
              onChange={(event) => setForm({ ...form, is_active: event.target.checked })} />
            Active
          </label>
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button type="submit" disabled={busy || !form.name.trim()} className={`${buttonClass} bg-blue-500 text-white hover:bg-blue-600`}>
              {busy ? "Saving..." : "Save Brand"}
            </button>
            <button type="button" disabled={busy} onClick={() => { setForm(null); setError(""); }} className={`${buttonClass} border border-slate-200 text-slate-600 hover:bg-slate-50`}>Cancel</button>
          </div>
        </form>
        </CatalogModal>
      )}

      {deleting && (
        <section aria-label="Confirm brand deletion" className="mb-6 rounded-xl border border-red-200 bg-white p-5">
          <h2 className="font-semibold text-slate-900">Deactivate {deleting.name}?</h2>
          <p className="my-2 text-sm text-slate-500">This is a soft delete. The brand and its product links will remain saved. You can reactivate it by editing the brand.</p>
          <div className="flex gap-2">
            <button disabled={busy} onClick={deleteBrand} className={`${buttonClass} bg-red-600 text-white hover:bg-red-700`}>{busy ? "Deleting..." : "Confirm Delete"}</button>
            <button disabled={busy} onClick={() => setDeleting(null)} className={`${buttonClass} border border-slate-200 text-slate-600`}>Cancel</button>
          </div>
        </section>
      )}

      <section aria-label="Catalog brands" className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" aria-hidden="true" />
            <input aria-label="Search brands" placeholder="Search brands..." value={search}
              onChange={(event) => setSearch(event.target.value)} className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100" />
          </div>
          <div className="flex items-center gap-3">
            <select aria-label="Brand status" value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">
              <option value="">All statuses</option><option value="true">Active</option><option value="false">Inactive</option>
            </select>
            {!loading && !error && <span className="text-xs text-slate-500">{count} brands</span>}
          </div>
        </div>
        {loading ? <p role="status" className="p-12 text-center text-sm text-slate-500">Loading brands...</p> : error && !brands.length ? (
          <div className="p-12 text-center"><button onClick={refresh} className={`${buttonClass} border border-slate-200 text-slate-600`}>Retry loading brands</button></div>
        ) : !brands.length ? (
          <div className="p-12 text-center text-slate-500"><Badge className="mx-auto mb-3 h-8 w-8 text-violet-400" aria-hidden="true" /><p className="text-sm">No brands found.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-y border-slate-100 bg-slate-50 text-xs font-medium text-slate-500">
                <tr><th className="px-5 py-3">Brand name</th><th className="px-5 py-3">Slug</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {brands.map((brand) => (
                  <tr key={brand.id}>
                    <td className="px-5 py-4 font-medium text-slate-900">{brand.name}</td>
                    <td className="px-5 py-4 text-slate-500">{brand.slug_name}</td>
                    <td className="px-5 py-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${brand.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{brand.is_active ? "Active" : "Inactive"}</span></td>
                    <td className="px-5 py-4"><div className="flex justify-end gap-2">
                      <button aria-label={`Edit ${brand.name}`} disabled={busy} onClick={() => editBrand(brand)} className="rounded-lg border border-blue-200 p-2 text-blue-500 hover:bg-blue-50 disabled:opacity-50"><Pencil className="h-4 w-4" /></button>
                      <button aria-label={`Delete ${brand.name}`} disabled={busy || !brand.is_active} onClick={() => { setDeleting(brand); setForm(null); setError(""); setNotice(""); }} className="rounded-lg border border-red-200 p-2 text-red-500 hover:bg-red-50 disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && !error && count > 0 && (
          <nav aria-label="Brand pagination" className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 p-4 text-sm text-slate-500">
            <p>Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min((page - 1) * PAGE_SIZE + brands.length, count)} of {count} brands</p>
            <div className="flex items-center gap-3">
              <button disabled={!hasPrevious} onClick={() => setPage((value) => value - 1)} className={`${buttonClass} border border-slate-200`}>Previous</button>
              <span aria-live="polite">Page {page} of {Math.ceil(count / PAGE_SIZE)}</span>
              <button disabled={!hasNext} onClick={() => setPage((value) => value + 1)} className={`${buttonClass} border border-slate-200`}>Next</button>
            </div>
          </nav>
        )}
      </section>
    </div>
  );
}
